import React, { useState } from "react";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { Loader2, Calendar, Clock, TrendingUp, BookOpen, PenTool, ArrowLeft, Bell, Check } from "lucide-react";

const MainsRevisionSchedulerPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [topic, setTopic] = useState("");
  const [paperType, setPaperType] = useState("GS1");
  const [result, setResult] = useState(null);
  const [remindersSet, setRemindersSet] = useState({});

  const handleGenerateSchedule = async () => {
    if (!topic.trim()) {
      toast({
        variant: "destructive",
        description: "Please enter a topic for mains revision"
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.post(
        `${API_BASE}/essay/mains/revision/schedule`,
        {
          topic: topic,
          paper: paperType,
          last_studied: null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const schedule = response.data;
      setResult(schedule);
      toast({
        description: `Mains revision schedule generated for ${topic}!`
      });
    } catch (err) {
      console.error("Error:", err);
      toast({
        variant: "destructive",
        description: "Failed to generate revision schedule"
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateDate = (daysFromNow) => {
    const date = new Date();
    date.setDate(date.getDate() + daysFromNow);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleSetReminder = (cycleIdx, cycle) => {
    const dateStr = cycle.date === "Today" 
      ? new Date().toISOString().split('T')[0]
      : (() => {
          const date = new Date();
          date.setDate(date.getDate() + cycle.day - 1);
          return date.toISOString().split('T')[0];
        })();
    
    const eventTitle = `CLARIAS: Mains Revision - ${result.topic} (Cycle ${cycleIdx + 1})`;
    const eventDetails = `Write ${cycle.answers} answers (250 words each)${cycle.essays > 0 ? ` and ${cycle.essays} essay (1000 words)` : ''} for ${result.paper}`;
    
    // Create Google Calendar event URL
    const startDate = dateStr.replace(/-/g, '');
    const endDate = startDate;
    const googleCalendarUrl = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventTitle)}&dates=${startDate}/${endDate}&details=${encodeURIComponent(eventDetails)}&sf=true`;
    
    // Open Google Calendar in new tab
    window.open(googleCalendarUrl, '_blank');
    
    // Mark reminder as set
    setRemindersSet(prev => ({ ...prev, [cycleIdx]: true }));
    
    toast({
      description: `📅 Reminder set for Cycle ${cycleIdx + 1} - ${calculateDate(cycle.day - 1)}`
    });
  };

  const papers = [
    "GS1 - Indian Heritage & Culture",
    "GS2 - Governance & Polity",
    "GS3 - Economy & Environment",
    "GS4 - Ethics & Integrity",
    "Essay Paper"
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      {/* Back Button */}
      <Button variant="outline" onClick={() => navigate('/mains')} className="mb-2">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Mains Hub
      </Button>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Mains Revision Scheduler</h1>
          <p className="text-sm text-slate-600">
            Spaced repetition for answer writing & essay practice
          </p>
        </div>
        <Badge className="bg-green-500">
          🔓 FREE
        </Badge>
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Calendar className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-indigo-900 mb-1">
                Mains-Specific Revision Strategy:
              </p>
              <p className="text-sm text-slate-700">
                Focus on answer writing practice with decreasing frequency (Day 1, 3, 7, 14, 30). 
                Each cycle includes answer practice + essay writing. Builds muscle memory for 
                250-word answers and essay structure.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration Card */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="topic">Topic / Chapter</Label>
              <Input
                id="topic"
                placeholder="e.g., Indian Art & Culture, Welfare Schemes, Climate Change"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="paperType">Paper Type</Label>
              <select
                id="paperType"
                value={paperType}
                onChange={(e) => setPaperType(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 border border-slate-300 rounded-md"
              >
                {papers.map((paper, idx) => (
                  <option key={idx} value={`GS${idx + 1}`}>
                    {paper}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <Button
            onClick={handleGenerateSchedule}
            disabled={loading}
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-600"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generating Schedule...
              </>
            ) : (
              <>
                <Calendar className="w-4 h-4 mr-2" />
                Generate Mains Revision Schedule
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Topic Header */}
          <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <PenTool className="w-6 h-6 text-indigo-600" />
                <h3 className="text-lg font-bold text-slate-900">
                  Mains Revision Schedule: {result.topic}
                </h3>
              </div>
              <div className="flex items-center gap-4">
                <Badge className="bg-indigo-600">
                  {result.paper}
                </Badge>
                <Badge className="bg-purple-600">
                  {result.schedule?.length || 0} Revision Cycles
                </Badge>
                <Badge variant="secondary">
                  Forgetting Risk: {result.forgetting_probability}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Schedule Timeline */}
          {result.schedule && result.schedule.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-indigo-600" />
                  <h3 className="font-semibold text-slate-900">
                    Answer Writing & Essay Practice Timeline
                  </h3>
                </div>
                <div className="space-y-4">
                  {result.schedule.map((cycle, idx) => {
                    const dateStr = cycle.date === "Today" 
                      ? "Today" 
                      : calculateDate(cycle.day - 1);
                    
                    return (
                      <div
                        key={idx}
                        className={`border-l-4 ${
                          idx === 0 ? "border-l-green-500 bg-green-50" :
                          idx === 1 ? "border-l-blue-500 bg-blue-50" :
                          idx === 2 ? "border-l-purple-500 bg-purple-50" :
                          idx === 3 ? "border-l-orange-500 bg-orange-50" : 
                          "border-l-red-500 bg-red-50"
                        } rounded-r-lg p-4`}
                      >
                        <div className="flex items-center justify-between flex-wrap gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Badge
                                className={
                                  idx === 0 ? "bg-green-600" :
                                  idx === 1 ? "bg-blue-600" :
                                  idx === 2 ? "bg-purple-600" :
                                  idx === 3 ? "bg-orange-600" : "bg-red-600"
                                }
                              >
                                Cycle {idx + 1}
                              </Badge>
                              <span className="text-sm font-semibold text-slate-900">
                                Day {cycle.day}
                              </span>
                              <span className="text-sm text-slate-600">
                                ({dateStr})
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3 text-sm text-slate-700">
                              <div className="flex items-center gap-2">
                                <BookOpen className="w-4 h-4 text-indigo-600" />
                                <span>Write {cycle.answers} answers (250 words)</span>
                              </div>
                              {cycle.essays > 0 && (
                                <div className="flex items-center gap-2">
                                  <PenTool className="w-4 h-4 text-purple-600" />
                                  <span>Write {cycle.essays} essay (1000 words)</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <Button 
                            variant={remindersSet[idx] ? "default" : "outline"} 
                            size="sm"
                            onClick={() => handleSetReminder(idx, cycle)}
                            className={remindersSet[idx] ? "bg-green-600 hover:bg-green-700" : ""}
                          >
                            {remindersSet[idx] ? (
                              <>
                                <Check className="w-4 h-4 mr-1" />
                                Reminder Set
                              </>
                            ) : (
                              <>
                                <Bell className="w-4 h-4 mr-1" />
                                Set Reminder
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Strong & Weak Areas */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {result.strong_areas && result.strong_areas.length > 0 && (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-5 h-5 text-green-600" />
                    <h3 className="font-semibold text-slate-900">Strong Areas</h3>
                  </div>
                  <ul className="space-y-1">
                    {result.strong_areas.map((area, idx) => (
                      <li key={idx} className="text-sm text-slate-700 flex items-center gap-2">
                        <span className="text-green-600">✓</span>
                        {area}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {result.weak_areas && result.weak_areas.length > 0 && (
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-5 h-5 text-red-600 rotate-180" />
                    <h3 className="font-semibold text-slate-900">Areas to Improve</h3>
                  </div>
                  <ul className="space-y-1">
                    {result.weak_areas.map((area, idx) => (
                      <li key={idx} className="text-sm text-slate-700 flex items-center gap-2">
                        <span className="text-red-600">⚠</span>
                        {area}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Recommended Sources */}
          {result.recommended_sources && result.recommended_sources.length > 0 && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">
                  📚 Recommended Study Sources
                </h3>
                <div className="flex flex-wrap gap-2">
                  {result.recommended_sources.map((source, idx) => (
                    <Badge key={idx} variant="secondary" className="bg-white">
                      {source}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Mains-Specific Tips */}
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-amber-900 mb-2">
                💡 Mains Revision Tips
              </h3>
              <ul className="text-sm text-slate-700 space-y-1">
                <li>• Focus on answer structure: Introduction → Body → Conclusion</li>
                <li>• Practice diagrams, flowcharts, and maps in each cycle</li>
                <li>• Time yourself: 10 minutes for 250-word answers</li>
                <li>• Compare your answers with model answers after each cycle</li>
                <li>• Maintain an answer writing copy for consistent practice</li>
                <li>• Focus on presentation: clear headings, bullet points, underlining</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default MainsRevisionSchedulerPage;
